import type React from 'react';

import { Accordion, Badge, Button, FocusRing, Link, Text } from '@change/ds-core-ui';
import { ChatBubbleIcon, PlayArrowIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { PETITION_OPTIMIZATION_CALL_URL } from '#src/app/pages/ambassadors/landing/shared/consts';

import type { QuestionNumber } from './constants';
import { formatDurationLabel, POSTER_SRC, QUESTION_NUMBERS, VIDEO_DURATION_SECONDS } from './constants';
import { useVideoWalkthrough } from './hooks/useVideoWalkthrough';

// Translation keys must be string literals for `@change-corgi/no-invalid-translation-keys`, so each
// question's copy is mapped explicitly here rather than built from a dynamic `question_${number}` key.
const QUESTION_TRANSLATIONS: Readonly<Record<QuestionNumber, React.ReactNode>> = {
	1: <Translate value="corgi.ambassadors.video.question_1" />,
	2: <Translate value="corgi.ambassadors.video.question_2" />,
	3: <Translate value="corgi.ambassadors.video.question_3" />,
	4: <Translate value="corgi.ambassadors.video.question_4" />,
	5: <Translate value="corgi.ambassadors.video.question_5" />,
};

// Answer 3's trailing link is a separate key so it can use the design-system Link without HTML
// parsing — same approach as `HowItWorks`.
const QUESTION_ANSWERS: Readonly<Record<QuestionNumber, React.ReactNode>> = {
	1: <Translate value="corgi.ambassadors.video.answer_1" />,
	2: <Translate value="corgi.ambassadors.video.answer_2" />,
	3: (
		<>
			<Translate value="corgi.ambassadors.video.answer_3" />{' '}
			<Link
				href={PETITION_OPTIMIZATION_CALL_URL}
				target="_blank"
				rel="noopener noreferrer"
				variant="secondary"
				size="small"
				className="text-typography-weak inline"
			>
				<Translate value="corgi.ambassadors.video.answer_3_link" />
			</Link>
		</>
	),
	4: <Translate value="corgi.ambassadors.video.answer_4" />,
	5: <Translate value="corgi.ambassadors.video.answer_5" />,
};

type QuestionAccordionProps = Readonly<{
	questionNumber: QuestionNumber;
	expanded: boolean;
	onToggle: (questionNumber: QuestionNumber, expanded: boolean) => void;
	onWatchClick: (questionNumber: QuestionNumber) => void;
}>;

const QuestionAccordion: React.FC<QuestionAccordionProps> = ({ questionNumber, expanded, onToggle, onWatchClick }) => (
	<Accordion
		variant="un-contained"
		expanded={expanded}
		onToggle={(_event, isExpanded) => onToggle(questionNumber, isExpanded)}
		leadingIcon={<ChatBubbleIcon className="text-icon-brand shrink-0" />}
		title={
			<Text variant="body" size="medium" className="text-typography-strong">
				{QUESTION_TRANSLATIONS[questionNumber]}
			</Text>
		}
	>
		<div className="flex flex-col items-start gap-3 pb-2">
			<Text as="p" size="small" className="text-typography-weak max-w-prose">
				{QUESTION_ANSWERS[questionNumber]}
			</Text>
			<Button variant="tertiary" size="small" onClick={() => onWatchClick(questionNumber)}>
				<Translate value="corgi.ambassadors.video.watch_section" />
			</Button>
		</div>
	</Accordion>
);

export const VideoWalkthrough: React.FC = () => {
	const { translate } = useI18n();
	const { data, actions } = useVideoWalkthrough();
	const { videoRef, sectionRef, showOverlay, hasStarted, videoSrc, expandedQuestion } = data;
	const { handlePlayClick, handleVideoClick, handleQuestionClick, handleQuestionToggle, handleEnded } = actions;

	return (
		<section id="watch" ref={sectionRef}>
			<div className="container py-12 md:py-16">
				<Text size="small" className="text-stroke-brand-strong mb-1.5 block font-semibold tracking-wider uppercase">
					<Translate value="corgi.ambassadors.video.label" />
				</Text>
				<Text as="h2" variant="heading" size="large" fontWeight="bold" className="text-typography-strong mb-2 block">
					<Translate value="corgi.ambassadors.video.heading" />
				</Text>
				<Text variant="body" size="large" className="text-typography-weak mb-9 block">
					<Translate value="corgi.ambassadors.video.sub" />
				</Text>

				{/* Both cells stretch to the row height and centre their own content, which centres
				    whichever of the two is shorter — the video against a tall answer column, or the
				    answer column against a tall video. */}
				<div className="border-stroke-weaker bg-background-base rounded-large grid grid-cols-1 overflow-hidden border md:grid-cols-[2fr_3fr]">
					<div className="flex items-center justify-center overflow-hidden bg-black">
						{/* Sized to the video, not the cell, so the overlay and caption track the frame rather
						    than the letterbox area around it. */}
						<div className="relative w-full">
							<video
								ref={videoRef}
								// Holds the video's own 720x1266 ratio at every breakpoint so it is only ever
								// width-constrained. Tracking the cell height instead crops a portrait video.
								className="aspect-[720/1266] w-full object-contain"
								poster={POSTER_SRC}
								preload="metadata"
								playsInline
								controls={hasStarted}
								onEnded={handleEnded}
								onClick={handleVideoClick}
							>
								{videoSrc ? <source src={videoSrc} type="video/mp4" /> : null}
							</video>
							{showOverlay && (
								<FocusRing focusRingClass="rounded-xs">
									<button
										type="button"
										onClick={handlePlayClick}
										aria-label={translate('corgi.ambassadors.video.play_aria_label')}
										className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 border-0 bg-transparent p-0"
									>
										<span className="absolute top-4 right-4 rounded-full bg-black/60 px-2 py-1 text-xs font-semibold text-white">
											{formatDurationLabel(VIDEO_DURATION_SECONDS)}
										</span>
										<span className="flex size-[72px] items-center justify-center rounded-full bg-white shadow-lg">
											<PlayArrowIcon size={32} className="text-icon-strong-fixed" />
										</span>
										<span className="rounded-full bg-black/70 px-4 py-1.5 text-sm font-semibold text-white">
											<Translate value="corgi.ambassadors.video.play_label" />
										</span>
										<span className="absolute right-0 bottom-4 left-0 text-center text-sm font-semibold text-white">
											<Translate value="corgi.ambassadors.video.caption" />
										</span>
									</button>
								</FocusRing>
							)}
						</div>
					</div>

					<div className="border-stroke-weaker flex flex-col justify-center p-6 md:border-l md:p-8">
						<div className="bg-background-raised rounded-medium mb-4 flex flex-col items-start gap-3 p-6">
							<Badge
								variant="neutral"
								emphasis="strong"
								size="small"
								label={<Translate value="corgi.ambassadors.video.overview_tag" />}
							/>
							<Text size="small" className="text-typography-strong block">
								<Translate value="corgi.ambassadors.video.tldr" />
							</Text>
						</div>

						<div className="divide-stroke-weaker border-stroke-weaker flex flex-col divide-y border-t">
							{QUESTION_NUMBERS.map((number) => (
								<QuestionAccordion
									key={number}
									questionNumber={number}
									expanded={expandedQuestion === number}
									onToggle={handleQuestionToggle}
									onWatchClick={handleQuestionClick}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
